import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "motion/react";
import { Pin, X } from "lucide-react";
import {
  NOTICE_CATEGORIES,
  NOTICE_PRIORITIES,
  NOTICE_STATUS,
  linkifyParts,
} from "./noticeUtils";
import { useBackToClose } from "../../utils/backToClose";

export const noticeButton = {
  primary:
    "inline-flex items-center justify-center gap-2 rounded-lg border border-[#2470B7] bg-[#2470B7] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#1f5f9b] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500",
  secondary:
    "inline-flex items-center justify-center gap-2 rounded-lg border border-app-primary px-4 py-2 text-sm font-semibold text-app-secondary transition hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60",
  danger:
    "inline-flex items-center justify-center gap-2 rounded-lg border border-red-600 bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 disabled:cursor-not-allowed disabled:opacity-60",
  icon:
    "inline-flex h-9 w-9 items-center justify-center rounded-lg text-app-muted transition-colors hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40",
};

export const noticeInput =
  "w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20";

/** Dialog frame in the app theme, portalled to <body>. Bottom sheet on phones. `bare` drops the title bar and
 *  body padding so the content can draw its own header (the post view). */
export function NoticeModal({ title, onClose, children, footer, width = "max-w-2xl", headerExtra, bare = false }) {
  useBackToClose(true, onClose);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/45 dark:bg-black/60" onClick={onClose} aria-hidden="true" />
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "tween", ease: [0.22, 1, 0.36, 1], duration: 0.22 }}
        className={`relative flex max-h-[92dvh] w-full ${width} flex-col rounded-t-2xl border border-app-primary bg-app-card text-app-primary shadow-2xl dark:shadow-black/50 sm:max-h-[88vh] sm:rounded-2xl`}
      >
        {!bare && (
          <div className="flex items-center justify-between gap-3 border-b border-app-primary px-5 py-3.5">
            <h3 className="min-w-0 truncate text-base font-semibold text-app-heading">{title}</h3>
            <div className="flex shrink-0 items-center gap-1">
              {headerExtra}
              <button type="button" onClick={onClose} className={noticeButton.icon} aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
        <div className={`min-h-0 flex-1 overflow-y-auto ${bare ? "" : "px-5 py-4"}`}>{children}</div>
        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-app-primary px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {footer}
          </div>
        )}
      </motion.div>
    </div>,
    document.body
  );
}

const chipBase = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold leading-5";

/** Category, priority (unless normal), pinned and — for HR/admins — status chips. */
export function NoticeChips({ notice, showStatus = false, showCategory = true, className = "" }) {
  const category = NOTICE_CATEGORIES[notice.category] || NOTICE_CATEGORIES.general;
  const priority = NOTICE_PRIORITIES[notice.priority];
  const status = NOTICE_STATUS[notice.status];
  const CategoryIcon = category.icon;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      {notice.isPinned && (
        <span className={`${chipBase} bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300`}>
          <Pin className="h-3 w-3" aria-hidden="true" /> Pinned
        </span>
      )}
      {priority && notice.priority !== "normal" && <span className={`${chipBase} ${priority.chip}`}>{priority.label}</span>}
      {showCategory && (
        <span className={`${chipBase} ${category.chip}`}>
          <CategoryIcon className="h-3 w-3" aria-hidden="true" /> {category.label}
        </span>
      )}
      {showStatus && status && notice.status !== "active" && <span className={`${chipBase} ${status.chip}`}>{status.label}</span>}
    </div>
  );
}

/** Notice text: plain text with line breaks; URLs become links. Never rendered as HTML. */
export function NoticeBody({ body, className = "" }) {
  return (
    <div className={`whitespace-pre-wrap break-words text-sm leading-relaxed text-app-secondary ${className}`}>
      {linkifyParts(body).map((part, i) =>
        typeof part === "string" ? (
          part
        ) : (
          <a
            key={i}
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-blue-600 underline-offset-2 hover:underline dark:text-sky-400"
          >
            {part.url}
          </a>
        )
      )}
    </div>
  );
}

/** Thin labelled progress bar (read / acknowledged rates). */
export function RateBar({ label, value, total, tone = "blue" }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  const bar = tone === "green" ? "bg-emerald-500" : "bg-blue-500";
  return (
    <div className="min-w-0 flex-1">
      <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
        <span className="font-medium text-app-secondary">{label}</span>
        <span className="tabular-nums text-app-muted">
          {value}/{total} · {pct}%
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-app-hover">
        <div className={`h-full rounded-full ${bar} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
